// EditScreen.jsx - Livreur Planning
import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, Button, Alert, Table, Badge, Modal } from "react-bootstrap";
import { useHistory, useParams } from "react-router-dom";
import ClickBack from "../../components/buttons/ClickBack";
import {
  fetchLivreurSchedule,
  updateLivreurSchedule,
  fetchLivreurCapacity,
  updateLivreurCapacity,
  fetchLivreurTimeOff,
  createTimeOff,
  updateTimeOffStatus,
} from "./dataService";
import { format } from "date-fns";

const DAYS_FR = {
  1: "Lundi",
  2: "Mardi",
  3: "Mercredi",
  4: "Jeudi",
  5: "Vendredi",
  6: "Samedi",
  7: "Dimanche",
};

function EditScreen() {
  const { id } = useParams();
  const history = useHistory();
  const [livreur, setLivreur] = useState(null);
  const [scheduleData, setScheduleData] = useState([]);
  const [capacityData, setCapacityData] = useState({
    default_max_deliveries_per_day: 10,
    default_max_distance_per_day_km: 100,
    avg_speed_kmh: 30,
    avg_stop_duration_minutes: 10,
  });
  const [timeOffs, setTimeOffs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showTimeOffModal, setShowTimeOffModal] = useState(false);
  const [newTimeOff, setNewTimeOff] = useState({
    type: "vacation",
    start_date: "",
    end_date: "",
    reason: "",
  });

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Fetch schedule
      const scheduleResp = await fetchLivreurSchedule(id);
      setLivreur(scheduleResp.livreur);
      setScheduleData(scheduleResp.weekly_schedule || []);

      // Fetch capacity
      const capacityResp = await fetchLivreurCapacity(id);
      setCapacityData(capacityResp.capacity || capacityData);

      // Fetch time-offs (pending + approved)
      const timeOffResp = await fetchLivreurTimeOff(id);
      setTimeOffs(timeOffResp.time_offs || []);
    } catch (err) {
      console.error("Error fetching data:", err);
      setError("Erreur lors du chargement des données");
    } finally {
      setLoading(false);
    }
  };

  const handleScheduleChange = (dayIndex, field, value) => {
    const updatedSchedule = [...scheduleData];
    updatedSchedule[dayIndex] = {
      ...updatedSchedule[dayIndex],
      [field]: field === "is_working" ? value === "true" : value,
    };
    setScheduleData(updatedSchedule);
  };

  const handleCapacityChange = (field, value) => {
    setCapacityData({
      ...capacityData,
      [field]: value,
    });
  };

  const handleSaveSchedule = async () => {
    try {
      setSaving(true);
      await updateLivreurSchedule(id, { schedules: scheduleData });
      alert("Planning sauvegardé avec succès");
      history.push(`/livreurs/planning/${id}/view`);
    } catch (err) {
      console.error("Error saving schedule:", err);
      alert("Erreur lors de la sauvegarde du planning");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveCapacity = async () => {
    try {
      setSaving(true);
      await updateLivreurCapacity(id, capacityData);
      alert("Capacités sauvegardées avec succès");
    } catch (err) {
      console.error("Error saving capacity:", err);
      alert("Erreur lors de la sauvegarde des capacités");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateTimeOff = async () => {
    try {
      await createTimeOff(id, newTimeOff);
      setShowTimeOffModal(false);
      setNewTimeOff({ type: "vacation", start_date: "", end_date: "", reason: "" });
      fetchData(); // Refresh
      alert("Demande de congé créée avec succès");
    } catch (err) {
      console.error("Error creating time-off:", err);
      alert("Erreur lors de la création du congé");
    }
  };

  const handleUpdateTimeOffStatus = async (timeOffId, status) => {
    try {
      await updateTimeOffStatus(timeOffId, status);
      fetchData(); // Refresh
      alert(`Congé ${status === "approved" ? "approuvé" : "refusé"} avec succès`);
    } catch (err) {
      console.error("Error updating time-off status:", err);
      alert("Erreur lors de la mise à jour du statut");
    }
  };

  if (loading) {
    return (
      <Container fluid className="p-4 text-center">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container fluid className="p-4">
        <Alert variant="danger">{error}</Alert>
        <Button onClick={() => history.goBack()}>Retour</Button>
      </Container>
    );
  }

  return (
    <Container fluid className="p-4">
      <Row className="mb-4">
        <Col>
          <ClickBack />
          <h2 className="mt-3">
            Modifier le planning de {livreur?.nom} {livreur?.prenom}
          </h2>
        </Col>
      </Row>

      {/* Weekly Schedule */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header className="bg-primary text-white">
              <h5 className="mb-0">Planning Hebdomadaire</h5>
            </Card.Header>
            <Card.Body>
              <Table striped bordered>
                <thead>
                  <tr>
                    <th>Jour</th>
                    <th>Travaille</th>
                    <th>Début</th>
                    <th>Fin</th>
                    <th>Pause début</th>
                    <th>Pause fin</th>
                  </tr>
                </thead>
                <tbody>
                  {scheduleData.map((day, index) => (
                    <tr key={day.day_of_week}>
                      <td><strong>{DAYS_FR[day.day_of_week]}</strong></td>
                      <td>
                        <Form.Select
                          value={day.is_working.toString()}
                          onChange={(e) =>
                            handleScheduleChange(index, "is_working", e.target.value)
                          }
                        >
                          <option value="true">Oui</option>
                          <option value="false">Non</option>
                        </Form.Select>
                      </td>
                      <td>
                        <Form.Control
                          type="time"
                          value={day.start_time || ""}
                          onChange={(e) =>
                            handleScheduleChange(index, "start_time", e.target.value)
                          }
                          disabled={!day.is_working}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="time"
                          value={day.end_time || ""}
                          onChange={(e) =>
                            handleScheduleChange(index, "end_time", e.target.value)
                          }
                          disabled={!day.is_working}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="time"
                          value={day.break_start_time || ""}
                          onChange={(e) =>
                            handleScheduleChange(index, "break_start_time", e.target.value)
                          }
                          disabled={!day.is_working}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="time"
                          value={day.break_end_time || ""}
                          onChange={(e) =>
                            handleScheduleChange(index, "break_end_time", e.target.value)
                          }
                          disabled={!day.is_working}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
              <Button variant="primary" onClick={handleSaveSchedule} disabled={saving}>
                {saving ? "Sauvegarde..." : "Sauvegarder le planning"}
              </Button>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Capacity */}
      <Row className="mb-4">
        <Col md={6}>
          <Card>
            <Card.Header className="bg-info text-white">
              <h5 className="mb-0">Capacités</h5>
            </Card.Header>
            <Card.Body>
              <Form.Group className="mb-3">
                <Form.Label>Max livraisons/jour</Form.Label>
                <Form.Control
                  type="number"
                  value={capacityData.default_max_deliveries_per_day}
                  onChange={(e) =>
                    handleCapacityChange("default_max_deliveries_per_day", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Max distance/jour (km)</Form.Label>
                <Form.Control
                  type="number"
                  value={capacityData.default_max_distance_per_day_km}
                  onChange={(e) =>
                    handleCapacityChange("default_max_distance_per_day_km", e.target.value)
                  }
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Vitesse moyenne (km/h)</Form.Label>
                <Form.Control
                  type="number"
                  value={capacityData.avg_speed_kmh}
                  onChange={(e) => handleCapacityChange("avg_speed_kmh", e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Durée arrêt moyenne (min)</Form.Label>
                <Form.Control
                  type="number"
                  value={capacityData.avg_stop_duration_minutes}
                  onChange={(e) =>
                    handleCapacityChange("avg_stop_duration_minutes", e.target.value)
                  }
                />
              </Form.Group>
              <Button variant="info" onClick={handleSaveCapacity} disabled={saving}>
                {saving ? "Sauvegarde..." : "Sauvegarder les capacités"}
              </Button>
            </Card.Body>
          </Card>
        </Col>

        {/* Time Off Management */}
        <Col md={6}>
          <Card>
            <Card.Header className="bg-warning text-dark d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Congés</h5>
              <Button size="sm" variant="dark" onClick={() => setShowTimeOffModal(true)}>
                + Ajouter
              </Button>
            </Card.Header>
            <Card.Body style={{ maxHeight: "400px", overflowY: "auto" }}>
              {timeOffs.length === 0 ? (
                <p className="text-muted">Aucun congé</p>
              ) : (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Du</th>
                      <th>Au</th>
                      <th>Statut</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {timeOffs.map((timeOff) => (
                      <tr key={timeOff.id}>
                        <td><Badge bg="secondary">{timeOff.type}</Badge></td>
                        <td>{format(new Date(timeOff.start_date), "dd/MM/yyyy")}</td>
                        <td>{format(new Date(timeOff.end_date), "dd/MM/yyyy")}</td>
                        <td>
                          <Badge
                            bg={
                              timeOff.status === "approved"
                                ? "success"
                                : timeOff.status === "rejected"
                                ? "danger"
                                : "warning"
                            }
                          >
                            {timeOff.status}
                          </Badge>
                        </td>
                        <td>
                          {timeOff.status === "pending" && (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => handleUpdateTimeOffStatus(timeOff.id, "approved")}
                                className="me-1"
                              >
                                ✓
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleUpdateTimeOffStatus(timeOff.id, "rejected")}
                              >
                                ✗
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Time Off Modal */}
      <Modal show={showTimeOffModal} onHide={() => setShowTimeOffModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Ajouter un congé</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Type</Form.Label>
            <Form.Select
              value={newTimeOff.type}
              onChange={(e) => setNewTimeOff({ ...newTimeOff, type: e.target.value })}
            >
              <option value="vacation">Congés payés</option>
              <option value="sick_leave">Arrêt maladie</option>
              <option value="training">Formation</option>
              <option value="other">Autre</option>
            </Form.Select>
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Date de début</Form.Label>
            <Form.Control
              type="date"
              value={newTimeOff.start_date}
              onChange={(e) => setNewTimeOff({ ...newTimeOff, start_date: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Date de fin</Form.Label>
            <Form.Control
              type="date"
              value={newTimeOff.end_date}
              onChange={(e) => setNewTimeOff({ ...newTimeOff, end_date: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Raison</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={newTimeOff.reason}
              onChange={(e) => setNewTimeOff({ ...newTimeOff, reason: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowTimeOffModal(false)}>
            Annuler
          </Button>
          <Button variant="primary" onClick={handleCreateTimeOff}>
            Créer
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default EditScreen;
